import { useFeatureToggle } from '@environment/useFeatureToggle';
import { usePolisGetPolisDetails } from '@services/polis-public/orval-api/polis-api/polis-api';
import { useQuery } from '@tanstack/vue-query';
import { computed, type MaybeRef, type MaybeRefOrGetter, toValue } from 'vue';
import { polisQueries } from './polisQueries';

export function usePolisDetails(
  polisnummer: MaybeRef<string>,
  options?: { enabled?: MaybeRefOrGetter<boolean> },
) {
  const managementEnabled = useFeatureToggle('PolisTonenViaManagementToggle');
  const enabled = computed(() => toValue(options?.enabled ?? true));

  const legacyQuery = useQuery({
    ...polisQueries.getDetails(polisnummer),
    select: (response) => response.data,
    staleTime: 60_000,
    retry: 3,
    enabled: computed(() => enabled.value && !managementEnabled.value),
  });

  const managementQuery = usePolisGetPolisDetails(polisnummer, {
    query: {
      select: ({ result }) => ({
        ...result,
        vrijerubrieken: result.vrijeRubrieken,
        premiepromillages: result.premiePromillages,
        gebouwOms: result.gebouwOmschrijving ?? null,
      }),
      staleTime: 60_000,
      retry: 3,
      enabled: computed(() => enabled.value && managementEnabled.value === true),
    },
  });

  return {
    data: computed(() =>
      managementEnabled.value ? managementQuery.data.value : legacyQuery.data.value,
    ),
    isPending: computed(() =>
      managementEnabled.value ? managementQuery.isPending.value : legacyQuery.isPending.value,
    ),
    isError: computed(() =>
      managementEnabled.value ? managementQuery.isError.value : legacyQuery.isError.value,
    ),
    error: computed(() =>
      managementEnabled.value ? managementQuery.error.value : legacyQuery.error.value,
    ),
  };
}
